
import React, { useState, useEffect } from 'react';
import { useShopFilter } from '../shops/hooks/useShopFilter';
import { useProductsData } from './hooks/useProductsData';
import ShopsSidebar from './components/ShopsSidebar';
import ProductsContent from './components/ProductsContent';
import ProductsHeader from './components/ProductsHeader';

interface ProductsViewProps {
  handlePageChange?: (pageId: string) => void;
}

const ProductsView: React.FC<ProductsViewProps> = ({ handlePageChange }) => {
  const {
    shops,
    isCollapsed,
    setIsCollapsed,
    filteredShops,
    searchTerm,
    setSearchTerm,
    isLoading: shopsLoading,
    isSingleSelectionMode,
    selectedShops,
    singleSelectedShop,
    toggleShopSelection,
    setSingleSelectedShop,
    refreshShops
  } = useShopFilter();

  // État pour suivre l'ID de boutique sélectionné
  const [activeShopId, setActiveShopId] = useState<string | undefined>(undefined);
  
  // Mettre à jour l'activeShopId lorsque singleSelectedShop change
  useEffect(() => {
    if (singleSelectedShop) {
      console.log("Setting active shop ID to:", singleSelectedShop);
      setActiveShopId(singleSelectedShop);
    } else {
      console.log("Clearing active shop ID");
      setActiveShopId(undefined);
    }
  }, [singleSelectedShop]);

  // Gestionnaire de changement de page avec ID de boutique
  const handleProductPageChange = (pageId: string) => {
    console.log("handleProductPageChange called with:", pageId);
    if (handlePageChange) {
      console.log("Navigating to product page with shop ID:", activeShopId);
      if (pageId === 'new' && activeShopId) {
        handlePageChange(`${pageId}?shopId=${activeShopId}`);
      } else {
        handlePageChange(pageId);
      }
    } else {
      console.error("handlePageChange function is not available in ProductsView");
    }
  };

  // Utiliser useProductsData avec l'activeShopId pour filtrer les produits
  const {
    products,
    loading: productsLoading,
    selectedProducts,
    editingProductId,
    handleAddProduct,
    handleEditProduct,
    handleDeleteProduct,
    handleBulkDeleteProducts,
    toggleProductSelection,
    toggleSelectAll,
    areAllSelected,
    fetchProducts,
    addingProduct,
  } = useProductsData({ 
    handlePageChange: handleProductPageChange,
    shopId: activeShopId
  });

  // Fonction d'ajout de produit
  const handleAddNewProduct = () => {
    console.log("ProductsView: handleAddNewProduct called");
    if (handlePageChange) {
      handleProductPageChange('new');
    } else {
      console.error("handlePageChange is not available - cannot navigate to new product form");
    }
  };

  const formatDate = (date: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(date).toLocaleDateString('fr-FR', options);
  };

  // Appliquer le filtre de boutique quand nécessaire
  const applyShopFilter = () => {
    console.log("Applying shop filter with selected shop:", singleSelectedShop);
    fetchProducts();
  };

  return (
    <div className="flex h-full">
      <ShopsSidebar
        shops={shops}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isSingleSelectionMode={isSingleSelectionMode}
        selectedShops={selectedShops}
        singleSelectedShop={singleSelectedShop}
        toggleShopSelection={toggleShopSelection}
        setSingleSelectedShop={setSingleSelectedShop}
        filterProductsByShops={applyShopFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleAddProduct={handleAddNewProduct}
        isLoading={shopsLoading}
        refreshShops={refreshShops}
      />
      
      <div className="flex-1 bg-pastel-blue-light dark:bg-[#121624]">
        <div className="container mx-auto px-4 py-6">
          <ProductsHeader 
            isAdding={addingProduct}
            shopId={activeShopId}
            selectedProducts={selectedProducts}
          />
          
          <ProductsContent
            products={products}
            loading={productsLoading}
            selectedProducts={selectedProducts}
            editingProductId={editingProductId}
            toggleProductSelection={toggleProductSelection}
            handleEditProduct={handleProductPageChange}
            handleDeleteProduct={handleDeleteProduct}
            handleBulkDeleteProducts={handleBulkDeleteProducts}
            handleAddProduct={handleAddNewProduct}
            formatDate={formatDate}
            toggleSelectAll={() => toggleSelectAll(products)}
            areAllSelected={areAllSelected(products)}
            fetchProducts={fetchProducts}
            addingProduct={addingProduct}
            shopId={activeShopId}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductsView;
